import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ChatBubbleOutline, Fullscreen, Close } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Typography, Modal, Box, IconButton } from '@mui/material';
import { usePromptsBlocks, useUpdatePrompt } from '../hooks/prompts';
import Button from 'newComponents/Button';
import { useNotifyAlert } from 'context/NotifyAlert';
import { getTenantId, getWhatsappUserInfo } from 'util/isAdmin';
import * as S from '../styles';
import { SaveButton } from '../../Departments/NewDepartmentModal/styles';

export const PromptCustomization = () => {
	const tenantId = getTenantId();
    const isAdmin = getWhatsappUserInfo()?.profile_type_id === 1;
	const { promptsBlocks, isLoading } = usePromptsBlocks( tenantId );
	const { updatePrompt, isLoading: isUpdating } = useUpdatePrompt();
	const { setShowNotify } = useNotifyAlert();
	const [ fullscreenModal, setFullscreenModal ] = useState( { open: false, blockId: null, content: '', title: '' } );

	const [ expanded, setExpanded ] = React.useState( false );

	const handleExpand = ( panel ) => ( event, isExpanded ) => {
		setExpanded( isExpanded ? panel : false );
	};

	const { control, reset, watch, setValue } = useForm( {
		defaultValues: {}
	} );

	// Initialize form values when promptsBlocks data is loaded
	useEffect( () => {
		if ( promptsBlocks && promptsBlocks.length > 0 ) {
			const initialValues = {};
			promptsBlocks.forEach( block => {
				initialValues[`prompt_${block.id}`] = block.prompt || '';
			} );
			reset( initialValues );
		}
	}, [ promptsBlocks, reset ] );

	const handleUpdatePrompt = async ( block ) => {
		try {
			const formValues = watch();
			const promptContent = formValues[`prompt_${block.id}`];

			const updatedPrompt = {
				...block,
				prompt           : promptContent,
				prompt_blocks_id : block.id,
				tenant_id        : tenantId
			};

			await updatePrompt( updatedPrompt );
			setShowNotify( { status: 'success', text: 'Prompt atualizado com sucesso!' } );
		} catch ( error ) {
			setShowNotify( { status: 'error', text: 'Erro ao atualizar prompt.' } );
		}
	};

	const handleOpenFullscreen = ( block, event ) => {
		// Previne que o evento se propague para o accordion
		if ( event ) {
			event.stopPropagation();
		}

		const currentContent = watch( `prompt_${block.id}` ) || block.content || '';
		setFullscreenModal( {
			title   : block.name,
			open    : true,
			blockId : block.id,
			content : currentContent
		} );
	};

	const handleCloseFullscreen = () => {
		setFullscreenModal( { open: false, blockId: null, content: '', title: '' } );
	};

	const handleSaveFullscreen = () => {
		if ( fullscreenModal.blockId ) {
			setValue( `prompt_${fullscreenModal.blockId}`, fullscreenModal.content );
		}
		handleCloseFullscreen();
	};

	return (
		<>
			<div style={{ border: '1px solid #e0e0e0', borderRadius: '12px', padding: '22px' }} className='bg-white d-flex flex-column justify-content-between'>
				<div className='d-flex align-items-center' style={{ gap: '10px' }}>
					<ChatBubbleOutline />
					<h3 className='mb-0'>Customização de prompts</h3>
				</div>

				<div style={{ gap: '10px', marginTop: '26px' }}>
					{isLoading && <div>Carregando...</div>}
					{!isLoading && promptsBlocks && promptsBlocks.map( ( block ) => (
						<Accordion key={block.id} expanded={expanded === block.id} onChange={handleExpand( block.id )}>
							<AccordionSummary className='px-3 d-flex align-items-center justify-content-between w-full'>
								<Typography>{block.name}</Typography>
								{expanded === block.id && (
									<IconButton
										onClick={( event ) => handleOpenFullscreen( block, event )}
										style={{
											position        : 'absolute',
											top             : '8px',
											right           : '8px',
											backgroundColor : 'rgba(255, 255, 255, 0.8)',
											borderRadius    : '4px',
											padding         : '4px'
										}}
										size="small"
									>
										<Fullscreen fontSize="small" />
									</IconButton>
								)}
							</AccordionSummary>
							<AccordionDetails className='d-flex flex-column' style={{ gap: '10px' }}>
								<div style={{ position: 'relative' }}>
									<Controller
										name={`prompt_${block.id}`}
										control={control}
										defaultValue={block.content || ''}
										render={( { field } ) => (
											<S.PromptTextarea
												{...field}
												disabled={!isAdmin}
												rows={5}
												style={{
													width        : '100%',
													border       : '1px solid #e0e0e0',
													borderRadius : '12px',
													padding      : '12px',
													fontFamily   : 'inherit',
													fontSize     : '14px',
													resize       : 'vertical'
												}}
												placeholder="Digite o conteúdo do prompt..."
											/>
										)}
									/>
								</div>
								<SaveButton
									style={{ width: '100%' }}
									onClick={() => handleUpdatePrompt( block )}
									disabled={isUpdating}
								>
									{isUpdating ? 'Salvando...' : 'Salvar'}
								</SaveButton>
							</AccordionDetails>
						</Accordion>
					) )}
				</div>
			</div>

			{/* Modal de tela cheia */}
			<Modal
				open={fullscreenModal.open}
				onClose={handleCloseFullscreen}
				style={{
					display        : 'flex',
					alignItems     : 'center',
					justifyContent : 'center'
				}}
			>
				<Box
					style={{
						width           : '90vw',
						height          : '90vh',
						backgroundColor : 'white',
						borderRadius    : '12px',
						padding         : '24px',
						outline         : 'none',
						display         : 'flex',
						flexDirection   : 'column'
					}}
				>
					<div style={{
						display        : 'flex',
						justifyContent : 'space-between',
						alignItems     : 'center',
						marginBottom   : '20px',
						borderBottom   : '1px solid #e0e0e0',
						paddingBottom  : '16px'
					}}>
						<h2 style={{ margin: 0 }}>{fullscreenModal.title}</h2>
						<IconButton onClick={handleCloseFullscreen}>
							<Close />
						</IconButton>
					</div>

					<textarea
						disabled={!isAdmin}
						value={fullscreenModal.content}
						onChange={( e ) => setFullscreenModal( prev => ( { ...prev, content: e.target.value } ) )}
						placeholder="Digite o conteúdo do prompt..."
						style={{
							flex         : 1,
							width        : '100%',
							border       : '1px solid #e0e0e0',
							borderRadius : '8px',
							padding      : '16px',
							fontFamily   : 'inherit',
							fontSize     : '16px',
							resize       : 'none',
							outline      : 'none'
						}}
					/>

					<div style={{
						display        : 'flex',
						gap            : '12px',
						marginTop      : '20px',
						justifyContent : 'flex-end'
					}}>
						<Button
							variant="outline"
							onClick={handleCloseFullscreen}
							style={{ minWidth: '100px' }}
						>
							Cancelar
						</Button>
						<Button
							onClick={handleSaveFullscreen}
							style={{ minWidth: '100px' }}
						>
							Aplicar
						</Button>
					</div>
				</Box>
			</Modal>
		</>
	);
};

export default PromptCustomization;
